import { useState, useEffect, useRef } from 'react';
import { Card, Table, Spin, Tooltip, message, Typography, Button } from 'antd';
import { DownloadOutlined, FullscreenOutlined } from '@ant-design/icons';
import { Link, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { CSVLink } from 'react-csv';
import { useAppSelector } from '../../methods/redux/hooks';
import NativeNumber from '../../component/Numeral';
import { truncateAddress } from '../../methods/utils/general-utils';
import { DownloadButton } from './SingleRequestLogPage';
// import { triggerSwitchWalletRemittance } from '../../methods/redux/reducers/global-webhooks';
import getExplorerLink from './getExplorerLink';
import getExplorerTxLink from './getExplorerTxLink';

const { Text } = Typography;

interface RemittanceProps {
	transactions: any;
	rescanTransaction: (e: any) => void;
}

// interface TriggerRemittanceProps {
// 	merchantId: string;
// 	network?: string;
// 	currency?: string;
// 	walletAddresses: string[];
// }

const SwitchWalletTransactions = ({
	transactions,
	rescanTransaction,
}: RemittanceProps) => {
	// const dispatch = useAppDispatch();
	const [searchParams] = useSearchParams();
	const merchantIdParams = searchParams.get('merchantId') || '';

	const [results, setResults] = useState([] as any);
	const [shouldDownload, setShouldDownload] = useState(false);
	const [activeTransaction, setActiveTransaction] = useState('');
	const [pagination, setPagination] = useState({
		current: 1,
		pageSize: 10,
	});

	const { remittanceReport, loadingReport, rescanningTransaction } =
		useAppSelector((store) => store.globalWebhooks);

	const csvLinkRef = useRef<
		CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
	>(null);

	const reportStart = remittanceReport?.data?.reportPeriodStart
		? moment
				.utc(remittanceReport?.data?.reportPeriodStart)
				.format('YYYY-MM-DD HH:mm:ss')
		: '--';

	const reportEnd = remittanceReport?.data?.reportPeriodEnd
		? moment
				.utc(remittanceReport?.data?.reportPeriodEnd)
				.format('YYYY-MM-DD HH:mm:ss')
		: '--';

	// const triggerRemittance = async (record: any) => {
	// 	const triggerState: TriggerRemittanceProps = {
	// 		merchantId: merchantIdParams,
	// 		walletAddresses: [record.walletAddress],
	// 		network: record.network,
	// 		currency: record.currency,
	// 	};

	// 	try {
	// 		await dispatch(triggerSwitchWalletRemittance(triggerState));
	// 	} catch (error: any) {
	// 		message.error(error.response.data.message);
	// 	}
	// };
	const columns = [
		{
			title: 'S/N',
			dataIndex: 'number',
			key: 'number',
			render: (_: any, __: any, index: number) => (
				<strong>
					{(pagination.current - 1) * pagination.pageSize + index + 1}
				</strong>
			),
		},
		// {
		// 	title: 'Action',
		// 	dataIndex: 'action',
		// 	key: 'action',
		// 	render: (_: any, record: any) => (
		// 		<div>
		// 			<Popconfirm
		// 				title="Are you sure?"
		// 				onConfirm={() => triggerRemittance(record)}
		// 			>
		// 				<Button type="primary">Trigger Remittance</Button>
		// 			</Popconfirm>
		// 		</div>
		// 	),
		// },
		{
			title: 'Amount',
			dataIndex: 'amount',
			key: 'amount',
			render: (amount: any, record: any) => (
				<strong>
					<NativeNumber value={amount} decimalPlaces={4} />{' '}
					<Text strong>{record?.currency}</Text>
				</strong>
			),
		},
		{
			title: 'Currency',
			dataIndex: 'currency',
			key: 'currency',
		},
		{
			title: 'Network',
			dataIndex: 'network',
			key: 'network',
		},
		{
			title: 'Wallet Address',
			dataIndex: 'walletAddress',
			key: 'walletAddress',
			render: (walletAddress: string, record: any) => (
				<>
					<Tooltip title="Click to copy address" color="green">
						<span
							role="none"
							onClick={() => {
								navigator.clipboard.writeText(walletAddress);
								message.success('Address copied');
							}}
						>
							{truncateAddress(walletAddress)}
						</span>
					</Tooltip>
					<Tooltip title="View on Explorer" color="blue">
						<Link
							to={getExplorerLink(record.network, walletAddress)}
							target="_blank"
							rel="noreferrer"
						>
							<FullscreenOutlined />
						</Link>
					</Tooltip>
					<Link
						to={`/compliance/switch-wallet/wallet-balances?merchantId=${merchantIdParams}&walletAddress=${walletAddress}`}
						target="_blank"
						rel="noreferrer"
					>
						<Button type="default">View Balance</Button>
					</Link>

					<Link
						to={`/users/all-users/${walletAddress}`}
						target="_blank"
						rel="noreferrer"
					>
						<Button type="default">View User</Button>
					</Link>
				</>
			),
		},
		{
			title: 'Transaction Hash',
			dataIndex: 'transactionHash',
			key: 'transactionHash',
			render: (transactionHash: string, record: any) => (
				<div>
					<Tooltip title="Click to view transaction on explorer" color="green">
						{/* <span
							role="none"
							onClick={() => {
								navigator.clipboard.writeText(transactionHash);
								message.success('Transaction hash copied');
							}}
						>
							<Text code>
								{transactionHash.slice(0, 4)}...
								{transactionHash.slice(-4)}
							</Text>
						</span> */}
						<Link
							to={getExplorerTxLink(record.network, transactionHash)}
							target="_blank"
							rel="noreferrer"
						>
							<Text code>
								{transactionHash.slice(0, 4)}...
								{transactionHash.slice(-4)}
							</Text>
						</Link>
					</Tooltip>
					<Tooltip
						title="Rescan if deposit have not entered user wallet"
						color="blue"
					>
						<Button
							type="default"
							disabled={rescanningTransaction}
							onClick={() => {
								rescanTransaction(record);
								setActiveTransaction(transactionHash);
							}}
						>
							{rescanningTransaction &&
							activeTransaction === transactionHash ? (
								<Spin size="small" style={{ display: 'inline-block' }} />
							) : (
								'Rescan'
							)}
						</Button>
					</Tooltip>
				</div>
			),
		},
		{
			title: 'Transaction Time',
			dataIndex: 'transactionTime',
			key: 'transactionTime',
			render: (transactionTime: string) => (
				<p>
					{transactionTime
						? moment(transactionTime).format('MMM DD, YY / hh:mm:ss a')
						: '--'}
				</p>
			),
		},
	];

	// Transform the results data to match the headers
	const transformTransactionRequests = (requests: any[]) =>
		requests?.map((data: any, index) => ({
			sn: index + 1,
			amount: `${data?.amount} ${data?.currency}`,
			currency: data?.currency,
			network: data?.network,
			walletAddress: `https://bscscan.com/address/${data?.walletAddress}`,
			transactionHash: data?.transactionHash,
			transactionTime: moment(data.transactionTime).format(
				'MMM DD, YY / hh:mm:ss a'
			),
		}));

	// Define headers with simple keys
	const headers = [
		{ label: 'S/N', key: 'sn' },
		{ label: 'Amount', key: 'amount' },
		{ label: 'Currency', key: 'currency' },
		{ label: 'Network', key: 'network' },
		{ label: 'Wallet Address', key: 'walletAddress' },
		{ label: 'Transaction Hash', key: 'transactionHash' },
		{ label: 'Transaction Time', key: 'transactionTime' },
	];

	const handleDownload = () => {
		setResults([]);
		if (transactions?.length > 0) {
			const transformedData = transformTransactionRequests(transactions);
			setResults(transformedData);
			setShouldDownload(true);
		}
	};

	useEffect(() => {
		if (shouldDownload && csvLinkRef.current && results.length > 0) {
			csvLinkRef?.current?.link.click();
		}
	}, [results, shouldDownload]);

	return (
		<Card
			title={
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<span>Transactions</span>
					{transactions && transactions?.length > 0 && (
						<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
							<DownloadButton
								danger
								icon={<DownloadOutlined />}
								onClick={() => handleDownload()}
							>
								Download CSV
							</DownloadButton>
							<CSVLink
								data={results}
								headers={headers}
								className="download"
								filename={`Switch_Wallet_Transactions ${reportStart}-${reportEnd}.csv`}
								ref={csvLinkRef}
							/>
						</div>
					)}
				</div>
			}
		>
			{loadingReport ? (
				<Spin size="large" style={{ display: 'block', margin: '40px auto' }} />
			) : (
				<Table
					rowKey="remittanceId"
					dataSource={transactions}
					columns={columns}
					pagination={{
						...pagination,
						onChange: (page, pageSize) => {
							setPagination({ current: page, pageSize });
						},
					}}
					scroll={{ x: 'max-content' }}
				/>
			)}
		</Card>
	);
};

export default SwitchWalletTransactions;
